import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import { CheckCircle, Star, Users, ChevronDown, ChevronUp } from 'lucide-react';

const AWSInterviewMasteryPage = () => {
  const [expandedModules, setExpandedModules] = useState({});

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleModule = (index) => {
    setExpandedModules(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  const courseModules = [
    {
      title: "Introduction",
      lessons: [
        "Overview & About Instructor",
        "Course Objectives & Prerequisites"
      ]
    },
    {
      title: "AWS Fundamentals", 
      lessons: [
        "What is AWS and why should you learn it?",
        "Dive Deeper: Recommended Courses & Content"
      ]
    },
    {
      title: "Job Roles in the Cloud",
      lessons: [
        "Overview of Cloud Computing Jobs",
        "Solutions Architect",
        "Cloud Support Engineer", 
        "Technical Account Manager"
      ]
    },
    {
      title: "Building Your Resume",
      lessons: [
        "How to Create a Stand-out Cloud Resume",
        "Cloud Computing Resume Template"
      ]
    },
    {
      title: "AWS Interviews: What to expect",
      lessons: [
        "Overview of AWS Interviews",
        "Phone Screens",
        "Technical Interviews",
        "Solutions Architect",
        "Cloud Support Engineer",
        "Technical Account Manager",
        "Amazon's 16 Leadership Principles",
        "Behavioral Interviews"
      ]
    },
    {
      title: "How to Answer Common Interview Questions",
      lessons: [
        "How to Answer: \"Tell Me About Yourself\"",
        "How to Answer: \"Why Amazon?\"",
        "How to Answer: Scenario Based Questions"
      ]
    },
    {
      title: "AWS Interviews: Time to practice",
      lessons: [
        "Interview Tips & Strategies",
        "Practice Questions (Behavioral Interview)",
        "Mock Behavioral Interview Example (With Amazon Employee)",
        "Practice Questions (Technical Interview)",
        "Mock Technical Interview Example (With AWS Employee)",
        "Interview Preparation Checklist"
      ]
    },
    {
      title: "Conclusion",
      lessons: [
        "Next Steps"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PromoBanner />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Course Info */}
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-cyan-600 mb-6">
                  AWS Interview Mastery
                </h1>
                
                <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                  Want to get hired in the Cloud? Perhaps you have an interview coming up, or you just want to get a head start.
                </p>
                
                <p className="text-lg text-gray-600 mb-4 leading-relaxed">
                  As a former AWS Solutions Architect, I share my deep knowledge of the Interview Process at Cloud companies.
                </p>
                
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Practice questions + mock interview examples included!
                </p>
                
                <div className="flex items-center gap-2 mb-8">
                  <Users size={20} className="text-blue-500" />
                  <span className="text-lg font-medium text-gray-700">Over 500 enrolments!</span>
                  <Star size={20} className="text-yellow-500 fill-current" />
                </div>
                
                <a 
                  href="https://learn.zerotocloud.co/p/aws-interview-mastery"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200 inline-block text-center"
                >
                  Enroll now for $129
                </a>
              </div>
              
              {/* Right Side - Course Thumbnail */}
              <div className="relative">
                <img 
                  src="/course-covers/1.png"
                  alt="AWS Interview Mastery Course"
                  className="w-full rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Content Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-cyan-600 mb-12 text-center">
              Course Content
            </h2>
            
            <div className="space-y-4">
              {courseModules.map((module, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                  <button
                    onClick={() => toggleModule(index)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-all duration-200 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-blue-600 font-semibold text-lg">
                        {index + 1}.
                      </span>
                      <h3 className="text-gray-900 font-medium">
                        {module.title}
                      </h3>
                    </div>
                    <div className="text-blue-600">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" className={`transform transition-transform duration-200 ${expandedModules[index] ? 'rotate-90' : ''}`}>
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </button>
                  
                  {expandedModules[index] && (
                    <div className="px-4 pb-4 bg-gray-50 border-t border-gray-200">
                      <ul className="space-y-3 pt-4">
                        {module.lessons.map((lesson, lessonIndex) => (
                          <li key={lessonIndex} className="flex items-start gap-3">
                            <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                            <span className="text-gray-700">{lesson}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Start Learning Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-cyan-600 mb-6">
              Ready to start learning?
            </h2>
            
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
              <div className="mb-6">
                <div className="text-4xl font-bold text-gray-900 mb-2">$129</div>
                <p className="text-gray-600">One-time payment • Lifetime access</p>
              </div>
              
              <a 
                href="https://learn.zerotocloud.co/p/aws-interview-mastery"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold text-xl hover:shadow-lg transition-all duration-200 mb-4 inline-block text-center"
              >
                Enroll now for $129
              </a>
              
              <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                <Users size={16} className="text-blue-500" />
                <span>Join 500+ students already enrolled</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AWSInterviewMasteryPage;